// Urmarirea actiunilor din interfata (ca in Google Analytics, dar la noi):
// click-uri pe butoane / linkuri / meniuri / tab-uri, paginile deschise,
// formularele trimise si erorile JS. Evenimentele se strang intr-o coada si
// se trimit in loturi la backend (jurnalul de audit, tabela ClientEvent),
// impreuna cu id-ul sesiunii din acest tab - acelasi id merge si pe
// request-urile API (antetele X-Session / X-Page / X-App), ca sa se poata
// lega "ce a apasat" de "ce request a plecat".
//
// Nu se inregistreaza NICIODATA ce se tasteaza in campuri (doar numele
// campurilor la trimiterea unui formular), si nici parole / token-uri.
const SESSION_KEY = "audit_sid";
const FLUSH_MS = 4000;
const BATCH = 50;
const MAX_QUEUE = 300;

// elementele pe care un click conteaza (restul click-urilor se ignora)
const CLICKABLE = [
  "button",
  "a",
  "[role='button']",
  "[role='menuitem']",
  "[role='tab']",
  "[role='option']",
  "[role='switch']",
  "[role='checkbox']",
  "[role='radio']",
  "input[type='submit']",
  "input[type='checkbox']",
  "input[type='radio']",
  "label",
  ".ant-select-item",
  ".ant-dropdown-menu-item",
  ".ant-menu-item",
  ".ant-tabs-tab",
  ".ant-pagination-item",
  ".ant-pagination-prev",
  ".ant-pagination-next",
  ".ant-table-row-expand-icon",
  ".ant-table-column-sorters",
  ".ant-table-column-has-sorters",
  ".ant-upload",
  ".ant-switch",
  ".ant-picker-cell",
  ".ant-picker-preset",
  ".ant-picker-presets li",
  ".ant-modal-close",
].join(", ");

// "invelisurile" care dau sensul unui click pe un <a> / <span> gol din
// interior (ex. numarul de pagina din paginare e un <a> cu textul "7")
const CONTAINERS = [
  ".ant-pagination-item",
  ".ant-pagination-prev",
  ".ant-pagination-next",
  ".ant-pagination-jump-prev",
  ".ant-pagination-jump-next",
  ".ant-menu-item",
  ".ant-menu-submenu-title",
  ".ant-tabs-tab",
  ".ant-select-item",
  ".ant-dropdown-menu-item",
  ".ant-table-column-has-sorters",
  ".ant-breadcrumb-link",
  ".ant-radio-button-wrapper",
  ".ant-segmented-item",
  ".ant-picker-preset",
  "li[role='menuitem']",
].join(", ");

// prefixe care explica ce fel de element a fost apasat
const KIND_LABELS = [
  [".ant-pagination-prev", () => "Pagina anterioară"],
  [".ant-pagination-next", () => "Pagina următoare"],
  [".ant-pagination-jump-prev", () => "Sari 5 pagini înapoi"],
  [".ant-pagination-jump-next", () => "Sari 5 pagini înainte"],
  [".ant-pagination-item", (t) => `Pagina ${t}`],
  [".ant-table-column-has-sorters", (t) => `Sortare: ${t}`],
  [".ant-tabs-tab", (t) => `Tab: ${t}`],
  [".ant-menu-item, .ant-menu-submenu-title", (t) => `Meniu: ${t}`],
  [".ant-select-item", (t) => `Opțiune: ${t}`],
  [".ant-dropdown-menu-item", (t) => `Meniu: ${t}`],
  [".ant-picker-preset", (t) => `Interval: ${t}`],
  [".ant-radio-button-wrapper, .ant-segmented-item", (t) => `Selectat: ${t}`],
];

const config = { app: "", endpoint: "", getToken: null };
let queue = [];
let timer = null;
let started = false;
let lastPage = null;
let memorySid = null;

function fallbackUuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function newUuid() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) {
    /* ignorat */
  }
  return fallbackUuid();
}

// id de sesiune per tab (sessionStorage); fara storage, unul per incarcare
export function getSessionId() {
  if (typeof window === "undefined") return null;
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = newUuid();
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (e) {
    if (!memorySid) memorySid = newUuid();
    return memorySid;
  }
}

// antetele de context pentru fiecare request API
export function trackHeaders() {
  if (typeof window === "undefined") return {};
  return {
    "X-App": config.app || "unknown",
    "X-Session": getSessionId(),
    "X-Page": window.location.pathname,
  };
}

const clean = (s, max) =>
  String(s || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);

function describe(el) {
  if (!el || !el.tagName) return null;
  let d = el.tagName.toLowerCase();
  if (el.id) d += `#${el.id}`;
  const classes = Array.from(el.classList || [])
    .filter((c) => !/^css-|^ant-btn-(default|primary)$/.test(c))
    .slice(0, 3);
  if (classes.length) d += `.${classes.join(".")}`;
  const dt = el.getAttribute && el.getAttribute("data-track");
  if (dt) d += ` [${dt}]`;
  return clean(d, 255);
}

function labelOf(el) {
  const attr = (n) => (el.getAttribute ? el.getAttribute(n) : null);
  const icon = el.querySelector ? el.querySelector("[aria-label]") : null;
  const text = clean(el.innerText || el.textContent, 120);
  if (el.matches) {
    for (const [sel, fn] of KIND_LABELS) {
      if (el.matches(sel)) return clean(fn(text || attr("title") || attr("aria-label") || ""), 160);
    }
  }
  return clean(
    attr("data-track") ||
      attr("aria-label") ||
      attr("title") ||
      text ||
      attr("placeholder") ||
      attr("name") ||
      (icon && icon.getAttribute("aria-label")) ||
      attr("href") ||
      el.id ||
      el.tagName,
    160
  );
}

export function track(type, label, extra = {}) {
  if (typeof window === "undefined") return;
  queue.push({
    type,
    label: clean(label, 160),
    page: window.location.pathname,
    ts: new Date().toISOString(),
    ...extra,
  });
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
  schedule();
}

export function trackPage(path, title) {
  if (typeof window === "undefined") return;
  const p = path || window.location.pathname;
  if (p === lastPage) return;
  lastPage = p;
  track("page", title || document.title || p, {
    page: p,
    meta: {
      referrer: clean(document.referrer, 200) || undefined,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      lang: navigator.language,
    },
  });
}

function onClick(e) {
  const t = e.target;
  if (!t || !t.closest) return;
  let el = t.closest(CLICKABLE);
  if (!el || el.closest("[data-track-ignore]")) return;
  // un <a>/<span> gol dintr-un element de paginare / meniu / tab: descriem invelisul
  const outer = el.closest(CONTAINERS);
  if (outer && outer !== el) el = outer;
  const meta = {};
  const href = el.getAttribute && el.getAttribute("href");
  if (href) meta.href = clean(href, 200);
  if (el.tagName === "INPUT" && (el.type === "checkbox" || el.type === "radio")) {
    meta.checked = !el.checked; // starea de dupa click
  }
  if (el.classList && el.classList.contains("ant-switch")) {
    meta.checked = el.getAttribute("aria-checked") !== "true";
  }
  track("click", labelOf(el), { target: describe(el), meta });
}

function onSubmit(e) {
  const form = e.target;
  if (!form || form.tagName !== "FORM") return;
  const fields = Array.from(form.elements || [])
    .map((f) => f.name || f.id)
    .filter(Boolean)
    .slice(0, 20);
  track("submit", form.getAttribute("name") || form.id || "form", {
    target: describe(form),
    meta: { fields },
  });
}

function onError(e) {
  const msg = e && e.message ? e.message : "Eroare JS";
  track("error", msg, {
    target: clean(e && e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : "", 255),
  });
}

function onRejection(e) {
  const r = e && e.reason;
  const msg = r && r.message ? r.message : typeof r === "string" ? r : "Promise respinsa";
  track("error", msg, { target: clean(r && r.stack ? r.stack.split("\n")[1] : "", 255) });
}

function schedule() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flush();
  }, FLUSH_MS);
}

export function flush() {
  if (typeof window === "undefined" || !queue.length || !config.endpoint) return;
  const events = queue.splice(0, BATCH);
  const headers = { "Content-Type": "application/json", ...trackHeaders() };
  try {
    const token = config.getToken && config.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (e) {
    /* ignorat */
  }
  try {
    // keepalive: lotul pleaca si daca pagina se inchide intre timp
    window
      .fetch(config.endpoint, {
        method: "POST",
        keepalive: true,
        headers,
        body: JSON.stringify({ app: config.app, events }),
      })
      .catch(() => {});
  } catch (e) {
    /* ignorat */
  }
  if (queue.length) schedule();
}

function patchHistory() {
  const wrap = (name) => {
    const orig = window.history[name];
    if (!orig || orig.__audit) return;
    const patched = function (...args) {
      const r = orig.apply(this, args);
      setTimeout(() => trackPage(), 0);
      return r;
    };
    patched.__audit = true;
    window.history[name] = patched;
  };
  wrap("pushState");
  wrap("replaceState");
  window.addEventListener("popstate", () => setTimeout(() => trackPage(), 0));
}

// initTracking({ app, endpoint, getToken, watchHistory })
//  app          - "admin" | "gestionar"
//  endpoint     - URL-ul la care se trimit loturile (POST)
//  getToken     - functie care intoarce JWT-ul curent (optional)
//  watchHistory - true: urmareste pushState/popstate pentru paginile vizitate
export function initTracking(opts = {}) {
  if (typeof window === "undefined" || started) return;
  started = true;
  config.app = opts.app || "";
  config.endpoint = opts.endpoint || "";
  config.getToken = opts.getToken || null;

  document.addEventListener("click", onClick, true);
  document.addEventListener("submit", onSubmit, true);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
  if (opts.watchHistory !== false) patchHistory();
  trackPage();
}
